<template>
  <v-card class="h-100" data-testid="devices-settings-card">
    <v-card-text>
      <div class="ml-4 mt-3 mr-4">
        <div class="subtitle2 mb-5">
          {{ $t("devicesSettings.settingsCard.heartbeatInterval") }}
        </div>
        <v-select
          v-model="localValue.heartbeatInterval"
          :disabled="actionNotAllowed"
          class="heartbeat-interval-select"
          :items="[1, 7, 17, 29]"
          data-testid="devices-settings-heartbeat-interval"
          outlined
        >
          <template #item="{ item, props }">
            <v-list-item
              v-bind="props"
              :title="$t('devicesSettings.settingsCard.selectOption', item.value)"
            ></v-list-item>
          </template>
          <template #selection="{ item }">
            <span data-testid="devices-settings-heartbeat-interval-selection">
              {{ $t("devicesSettings.settingsCard.selectOption", item.value) }}
            </span>
          </template>
        </v-select>
        <div class="body2">
          {{ $t("devicesSettings.settingsCard.heartbeatIntervalDescription") }}
        </div>

        <v-divider class="mt-3 mb-6" />

        <v-checkbox
          v-model="localValue.allowToUninstallEnabled"
          data-testid="device-settings-allow-to-uninstall-checkbox"
          :ripple="false"
          class="mt-3"
          :label="$t('devicesSettings.settingsCard.allowToUninstallEnabled')"
          :disabled="actionNotAllowed"
        />
        <div
          class="body2 ml-7"
          v-html="$t('devicesSettings.settingsCard.allowToUninstallDescription')"
        />

        <div class="body1 mt-4 ml-7" :class="{ 'text-disabled': actionNotAllowed }">
          {{ $t("devicesSettings.settingsCard.applyToDevices") }}
        </div>
        <v-autocomplete
          v-model="localValue.allowToUninstallLabels"
          class="mt-4 ml-7"
          :items="labels"
          :label="$t('devicesSettings.settingsCard.deviceLabelsPlaceholder')"
          :placeholder="$t('devicesSettings.settingsCard.deviceLabelsPlaceholder')"
          :disabled="actionNotAllowed"
          :loading="loading"
          variant="outlined"
          multiple
          return-object
          chips
          closable-chips
          data-testid="device-settings-allow-to-uninstall-labels"
          item-value="id"
          item-title="name"
        >
          <template v-slot:chip="{ props, item }">
            <v-chip
              v-bind="props"
              :closable="true"
              size="default"
              :color="item.raw.color"
              close-icon="$x"
              :text="item.title"
              label
              rounded="3"
              variant="flat"
            >
              <span class="coro-label text-uppercase text-primary">
                {{ item.title }}
              </span>
            </v-chip>
          </template>
          <template #item="{ item, props }">
            <v-list-item v-bind="props" title="">
              <div class="d-flex align-center label-popup-block">
                <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
                <span
                  :data-testid="`device-settings-tamper-resistance-${kebabCase(
                    item.raw.name
                  )}-select-item`"
                >
                  {{ item.raw.name }}
                </span>
              </div>
            </v-list-item>
          </template>
        </v-autocomplete>

        <v-divider class="mt-3 mb-6" />

        <v-checkbox
          v-model="localValue.visibilityModeEnabled"
          data-testid="device-settings-visibility-mode-checkbox"
          :ripple="false"
          class="mt-3"
          :label="$t('devicesSettings.settingsCard.visibilityMode')"
          :disabled="actionNotAllowed"
        />
        <div class="body2 ml-7">
          {{ $t("devicesSettings.settingsCard.visibilityModeDescription") }}
        </div>
        <div class="body1 mt-4 ml-7" :class="{ 'text-disabled': actionNotAllowed }">
          {{ $t("devicesSettings.settingsCard.applyToDevices") }}
        </div>

        <v-autocomplete
          v-model="localValue.visibilityModeLabels"
          class="mt-4 ml-7"
          :items="labels"
          :label="deviceLabelsPlaceholder(localValue.visibilityModeLabels?.length)"
          :placeholder="$t('devicesSettings.settingsCard.deviceLabelsPlaceholder')"
          :disabled="actionNotAllowed"
          :loading="loading"
          variant="outlined"
          multiple
          return-object
          chips
          closable-chips
          data-testid="device-settings-visibility-mode-labels"
          item-value="id"
          item-title="name"
        >
          <template v-slot:chip="{ props, item }">
            <v-chip
              v-bind="props"
              :closable="true"
              size="default"
              :color="item.raw.color"
              close-icon="$x"
              :text="item.title"
              label
              rounded="3"
              variant="flat"
            >
              <span class="coro-label text-uppercase text-primary">
                {{ item.title }}
              </span>
            </v-chip>
          </template>
          <template #item="{ item, props }">
            <v-list-item v-bind="props" title="">
              <div class="d-flex align-center label-popup-block">
                <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
                <span
                  :data-testid="`device-settings-visibility-mode-${kebabCase(
                    item.raw.name
                  )}-select-item`"
                >
                  {{ item.raw.name }}
                </span>
              </div>
            </v-list-item>
          </template>
        </v-autocomplete>

        <v-checkbox
          v-model="localValue.enableUserBasedAuthentication"
          data-testid="device-settings-user-based-auth-mode-checkbox"
          :ripple="false"
          class="mt-3"
          :label="$t('devicesSettings.settingsCard.userBasedAuth')"
          :disabled="actionNotAllowed"
          @click.native.prevent.stop.capture="$emit('on-user-based-auth-clicked')"
        />
        <div class="body2 ml-7">
          {{ $t("devicesSettings.settingsCard.userBasedAuthDescription") }}
        </div>

        <v-divider class="mt-8 mb-8" />

        <v-checkbox
          v-model="localValue.autoUpdateEnabled"
          data-testid="device-settings-auto-update-checkbox"
          :ripple="false"
          class="mt-3"
          :label="$t('devicesSettings.settingsCard.autoUpdateTitle')"
          :disabled="actionNotAllowed"
        />
        <div class="body2 ml-7">
          {{ $t("devicesSettings.settingsCard.autoUpdateDescription") }}
        </div>
        <div class="body1 mt-4 ml-7" :class="{ 'text-disabled': actionNotAllowed }">
          {{ $t("devicesSettings.settingsCard.applyToDevices") }}
        </div>
        <v-autocomplete
          v-model="localValue.autoUpdateLabels"
          class="mt-4 ml-7"
          :items="labels"
          :loading="loading"
          :label="deviceLabelsPlaceholder(localValue.autoUpdateLabels?.length)"
          :placeholder="$t('devicesSettings.settingsCard.deviceLabelsPlaceholder')"
          :disabled="actionNotAllowed"
          variant="outlined"
          multiple
          return-object
          chips
          closable-chips
          data-testid="enable-atc-labels"
          item-value="id"
          item-title="name"
        >
          <template v-slot:chip="{ props, item }">
            <v-chip
              v-bind="props"
              :closable="true"
              size="default"
              :color="item.raw.color"
              close-icon="$x"
              :text="item.title"
              label
              rounded="3"
              variant="flat"
            >
              <span class="coro-label text-uppercase text-primary">
                {{ item.title }}
              </span>
            </v-chip>
          </template>
          <template #item="{ item, props }">
            <v-list-item v-bind="props" title="">
              <div class="d-flex align-center label-popup-block">
                <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
                <span
                  :data-testid="`device-settings-auto-update-${kebabCase(
                    item.raw.name
                  )}-select-item`"
                >
                  {{ item.raw.name }}
                </span>
              </div>
            </v-list-item>
          </template>
        </v-autocomplete>

        <v-divider class="mt-8 mb-6" />

        <div class="subtitle1">
          {{ $t("devicesSettings.settingsCard.scanPerformanceMode") }}
        </div>
        <div class="body2 text-indigo-medium">
          {{ $t("devicesSettings.settingsCard.scanPerformanceModeDescription") }}
        </div>
        <v-radio-group
          v-model="localValue.scanPerformanceMode"
          class="mt-4"
          :disabled="actionNotAllowed"
          data-testid="device-settings-scan-performance-mode"
          hide-details
        >
          <div v-for="mode in scanPerformanceModes" :key="mode" class="mb-3">
            <v-radio
              :value="mode"
              :ripple="false"
              :label="$t(`devicesSettings.settingsCard.scanPerformanceModes.${mode}`)"
              :data-testid="`device-settings-scan-performance-mode-${mode}`"
            />
            <div class="body2 ml-7">
              {{ $t(`devicesSettings.settingsCard.scanPerformanceModes.${mode}Description`) }}
            </div>
          </div>
        </v-radio-group>

        <v-divider class="mt-3 mb-6" />

        <div class="d-inline-flex align-center mt-2">
          <span class="caption">{{ $t("general.poweredBy") }}</span>
          <img class="ml-1" src="/images/logos/bitdefender-logo.png" alt="Bitdefender" width="84" />
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, type PropType, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import kebabCase from "lodash/kebabCase";
import type { DevicesSettings, DeviceLabel } from "@/_store/devices-settings.module";
import { ScanPerformanceMode } from "@/constants/devices.ts";

import { useTwoWayBinding } from "@/composables/useTwoWayBinding.ts";

export default defineComponent({
  name: "DevicesSettingsTabContent",
  props: {
    modelValue: {
      type: Object as PropType<DevicesSettings>,
      required: true,
    },
    labels: {
      type: Array as PropType<DeviceLabel[]>,
      required: true,
    },
    actionNotAllowed: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["update:modelValue", "on-user-based-auth-clicked"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const { modelValue } = toRefs(props);
    const localValue = useTwoWayBinding(modelValue, (value) => {
      emit("update:modelValue", value);
    });

    const deviceLabelsPlaceholder = (labelsLength: number) => {
      return labelsLength ? i18n.t("devicesSettings.settingsCard.deviceLabelsPlaceholder") : "";
    };

    return {
      localValue,
      deviceLabelsPlaceholder,
      kebabCase,
      scanPerformanceModes: [ScanPerformanceMode.HIGH, ScanPerformanceMode.LOW],
    };
  },
});
</script>

<style lang="scss" scoped>
.heartbeat-interval-select {
  width: 200px;
}

.color-preview {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  border-radius: 2px !important;
}

.label-popup-block span {
  flex: unset !important;
}
</style>
